import React, { useEffect, useState, useCallback } from "react";
import { EmptyState, SkeletonList } from "../../components/ui";
import { Icon } from "../shared/components";
import { ensureArray, ensureObject } from "../shared/utils";

// ── Default new rule template ─────────────────────────────────────────────────
const EMPTY_FORM = {
  name: "",
  description: "",
  trigger_type: "new_conversation",
  trigger_conditions: {},
  actions: [{ type: "send_message", params: { message: "" } }],
  active: true,
};

function ActionEditor({ action, index, onChange, onRemove, meta }) {
  const actionTypes = ensureArray(meta?.action_types);
  const params = action.params || {};

  return (
    <div className="automationActionRow">
      <div className="automationActionIndex">{index + 1}</div>
      <div className="automationActionBody">
        <select
          value={action.type}
          onChange={(e) => onChange({ ...action, type: e.target.value, params: {} })}
        >
          {actionTypes.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>

        {action.type === "send_message" && (
          <textarea
            value={params.message || ""}
            placeholder="Texto da mensagem automática. Use {name} para o nome do contato."
            rows={2}
            onChange={(e) => onChange({ ...action, params: { ...params, message: e.target.value } })}
          />
        )}
        {action.type === "change_status" && (
          <select value={params.status || "atendimento"} onChange={(e) => onChange({ ...action, params: { ...params, status: e.target.value } })}>
            <option value="atendimento">Em atendimento</option>
            <option value="finalizado">Finalizado</option>
            <option value="novo">Novo</option>
          </select>
        )}
        {action.type === "add_tag" && (
          <input
            value={params.tag || ""}
            placeholder="Nome da etiqueta"
            onChange={(e) => onChange({ ...action, params: { ...params, tag: e.target.value } })}
          />
        )}
        {(action.type === "assign_to" || action.type === "create_lead") && (
          <p className="automationActionNote">Configuração avançada disponível em breve.</p>
        )}
        {action.type === "send_template" && (
          <input
            value={params.template_name || ""}
            placeholder="Nome do template aprovado"
            onChange={(e) => onChange({ ...action, params: { ...params, template_name: e.target.value } })}
          />
        )}
      </div>
      <button className="automationActionRemove" onClick={onRemove} title="Remover ação">
        <Icon name="trash" size={12} />
      </button>
    </div>
  );
}

function TriggerConditions({ triggerType, conditions, onChange }) {
  if (triggerType === "keyword_match") {
    return (
      <div className="automationConditions">
        <label>Palavras-chave <small>(separadas por vírgula)</small></label>
        <input
          value={conditions.keywords || ""}
          placeholder="preço, orçamento, urgente"
          onChange={(e) => onChange({ ...conditions, keywords: e.target.value })}
        />
      </div>
    );
  }
  if (triggerType === "no_reply") {
    return (
      <div className="automationConditions">
        <label>Tempo sem resposta (minutos)</label>
        <input
          type="number"
          value={conditions.minutes || 30}
          min={5}
          max={1440}
          onChange={(e) => onChange({ ...conditions, minutes: Number(e.target.value) })}
        />
      </div>
    );
  }
  if (triggerType === "status_changed") {
    return (
      <div className="automationConditions">
        <label>Status destino</label>
        <select value={conditions.status || "finalizado"} onChange={(e) => onChange({ ...conditions, status: e.target.value })}>
          <option value="novo">Novo</option>
          <option value="atendimento">Em atendimento</option>
          <option value="finalizado">Finalizado</option>
        </select>
      </div>
    );
  }
  return null;
}

// ── Editor modal ──────────────────────────────────────────────────────────────
function AutomationEditor({ automation, meta, onSave, onClose, saving }) {
  const [form, setForm] = useState(automation || EMPTY_FORM);
  const isNew = !automation?.id;

  function setField(key, value) { setForm((f) => ({ ...f, [key]: value })); }

  function addAction() {
    setForm((f) => ({
      ...f,
      actions: [...(f.actions || []), { type: "send_message", params: { message: "" } }],
    }));
  }

  function updateAction(index, updated) {
    setForm((f) => {
      const actions = [...(f.actions || [])];
      actions[index] = updated;
      return { ...f, actions };
    });
  }

  function removeAction(index) {
    setForm((f) => ({ ...f, actions: (f.actions || []).filter((_, i) => i !== index) }));
  }

  const triggerTypes = ensureArray(meta?.trigger_types);

  return (
    <div className="modalOverlay">
      <div className="modalBox automationEditorModal">
        <div className="modalHeader">
          <h3>{isNew ? "Nova automação" : "Editar automação"}</h3>
          <button className="ghostButton" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>

        <div className="automationForm">
          <label>Nome da automação *</label>
          <input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Ex: Boas-vindas para novos leads" />

          <label>Descrição</label>
          <input value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="O que esta automação faz?" />

          <div className="automationFormRow">
            <div>
              <label>Gatilho</label>
              <select value={form.trigger_type} onChange={(e) => setField("trigger_type", e.target.value)}>
                {triggerTypes.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <label className="automationToggleLabel">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setField("active", e.target.checked)}
                />
                <span>Ativa</span>
              </label>
            </div>
          </div>

          <TriggerConditions
            triggerType={form.trigger_type}
            conditions={form.trigger_conditions || {}}
            onChange={(c) => setField("trigger_conditions", c)}
          />

          <div className="automationActionsHeader">
            <label>Ações</label>
            <button type="button" className="ghostButton" onClick={addAction}>
              <Icon name="plus" size={12} /> Adicionar ação
            </button>
          </div>
          <div className="automationActionsList">
            {(form.actions || []).map((action, i) => (
              <ActionEditor
                key={i}
                action={action}
                index={i}
                meta={meta}
                onChange={(updated) => updateAction(i, updated)}
                onRemove={() => removeAction(i)}
              />
            ))}
            {(!form.actions || form.actions.length === 0) && (
              <p className="automationNoActions">Nenhuma ação configurada. Clique em "+ Adicionar ação".</p>
            )}
          </div>
        </div>

        <div className="modalActions">
          <button onClick={() => onSave(form)} disabled={saving || !form.name.trim()}>
            {saving ? "Salvando…" : isNew ? "Criar automação" : "Salvar alterações"}
          </button>
          <button className="secondaryBtn" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function AutomationsPage({ api }) {
  const [automations, setAutomations] = useState([]);
  const [meta, setMeta] = useState({ trigger_types: [], action_types: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | {id,...}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [autoRes, metaRes] = await Promise.all([
        api.get("/automations"),
        api.get("/automations/meta"),
      ]);
      setAutomations(ensureArray(autoRes.data));
      setMeta(ensureObject(metaRes.data));
    } catch {}
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function saveAutomation(form) {
    setSaving(true);
    setError("");
    try {
      if (form.id) {
        await api.put(`/automations/${form.id}`, form);
      } else {
        await api.post("/automations", form);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao salvar automação.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(automation) {
    try {
      await api.patch(`/automations/${automation.id}/toggle`);
      setAutomations((prev) => prev.map((a) => a.id === automation.id ? { ...a, active: !a.active } : a));
    } catch {}
  }

  async function deleteAutomation(automation) {
    if (!window.confirm(`Excluir "${automation.name}"?`)) return;
    try {
      await api.delete(`/automations/${automation.id}`);
      setAutomations((prev) => prev.filter((a) => a.id !== automation.id));
    } catch {}
  }

  const triggerLabel = (type) => {
    const found = meta.trigger_types?.find((t) => t.id === type);
    return found?.label || type;
  };

  const actionSummary = (actions) => {
    if (!actions?.length) return "Sem ações";
    const actionTypes = meta.action_types || [];
    return actions.map((a) => {
      const found = actionTypes.find((t) => t.id === a.type);
      return found?.label || a.type;
    }).join(" → ");
  };

  return (
    <div className="automationsPage">
      <div className="automationsTopBar">
        <div>
          <h2>Automações</h2>
          <p>Configure regras automáticas que disparam ações quando eventos específicos acontecem.</p>
        </div>
        <button onClick={() => setEditing(EMPTY_FORM)}>
          <Icon name="plus" size={14} /> Nova automação
        </button>
      </div>

      {error && <div className="errorBanner">{error}</div>}

      {loading ? (
        <SkeletonList rows={4} />
      ) : automations.length === 0 ? (
        <div className="automationsEmpty">
          <EmptyState illustration="flow" title="Nenhuma automação configurada" text="Crie regras automáticas para otimizar o atendimento. Ex: enviar mensagem de boas-vindas quando uma conversa nova entrar." />
          <button onClick={() => setEditing(EMPTY_FORM)}>
            <Icon name="plus" size={14} /> Criar primeira automação
          </button>
        </div>
      ) : (
        <div className="automationsList">
          {automations.map((a) => (
            <div key={a.id} className={`automationCard${a.active ? "" : " inactive"}`}>
              <div className="automationCardHead">
                <div className="automationCardInfo">
                  <strong>{a.name}</strong>
                  {a.description && <span>{a.description}</span>}
                </div>
                <div className="automationCardBadges">
                  <span className={`automationStatus${a.active ? " on" : " off"}`}>
                    {a.active ? "Ativa" : "Pausada"}
                  </span>
                  {a.run_count > 0 && <span className="automationRuns">{a.run_count} execuções</span>}
                </div>
              </div>
              <div className="automationCardBody">
                <div className="automationFlow">
                  <span className="automationTrigger">
                    <Icon name="zap" size={11} /> {triggerLabel(a.trigger_type)}
                  </span>
                  <span className="automationArrow">→</span>
                  <span className="automationActions">{actionSummary(a.actions)}</span>
                </div>
                {a.trigger_conditions?.keywords && (
                  <small>Palavras: {a.trigger_conditions.keywords}</small>
                )}
                {a.trigger_conditions?.minutes && (
                  <small>Após {a.trigger_conditions.minutes} min sem resposta</small>
                )}
              </div>
              <div className="automationCardActions">
                <button className="ghostButton" onClick={() => toggleActive(a)}>
                  {a.active ? "Pausar" : "Ativar"}
                </button>
                <button className="ghostButton" onClick={() => setEditing(a)}>
                  <Icon name="edit" size={12} /> Editar
                </button>
                <button className="ghostButton danger" onClick={() => deleteAutomation(a)}>
                  <Icon name="trash" size={12} /> Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <AutomationEditor
          automation={editing.id ? editing : null}
          meta={meta}
          onSave={saveAutomation}
          onClose={() => setEditing(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

export default AutomationsPage;
